import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { ReactComponent as ThreeDots } from "images/ThreeDots.svg";
import { ReactComponent as BackArrowIcon } from "images/back-arrow.svg";
import { ReactComponent as PlusBoldIcon } from "images/PlusBold.svg";
import { ReactComponent as LogoField } from "images/LogoField.svg";
import { ReactComponent as PlanetIcon } from "images/Planet.svg";
import { ReactComponent as CalendarIcon } from "images/CalendarIcon.svg";
import { ReactComponent as InfoIcon } from "images/infoIcon.svg";
import { Menu } from "./Menu/Menu";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Badge from "components-new/Badge";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import {
	changeStatusCategorization,
	editOrganization,
	getCodeList,
	getMarkerList,
	getOrganizationLogo,
	getSelectedOrganization,
	getSubsidiaries,
	switchCategorizationRule,
} from "toolkit/organizations/organizations.thunks";
import { ChangingLogo } from "./ChangingLogo/ChangingLogo";
import { ModalWindow } from "components/ModalWindow/ModalWindow";
import { ClearSelectedOrganization, setHistory, updateHistory } from "toolkit/organizations/organizations.slice";
import { connectNames, convertDate, isEmpty, replacePhone } from "helpers";
import { SettingMarkers } from "./SettingMarkers/SettingMarkers";
import { AddingOrganization } from "components/AddingOrganization/AddingOrganization";
import { ChoosingDate } from "components-new/ChoosingDate/ChoosingDate";
import Switch from "components-new/Switch";
import Tooltip from "components-new/Tooltip";
import AttentionModal from "./AttentionModal/AttentionModal";
import HistoryChanges from "./HistoryChanges/HistoryChanges";
import { IHistoryChange, IOrganizationShort } from "toolkit/organizations/types";
import styles from "./OrganizationPage.module.sass";
import cn from "classnames";
import ShortHistoryChanges from "./ShortHistoryChanges/ShortHistoryChanges";
import { usePagination } from "hooks/usePagination";
import InputTemp from "components-new/InputTemp";
import { MyTextarea } from "pages/StepPages/StepTwo/MyTextarea/MyTextarea";
import { OrganizationResponse } from "libs/apiClient/data-contracts";
import { organizationAPI } from "libs/apiClient/Organization";

interface IModalWindow {
	content: React.ReactElement | null;
	visibility: boolean;
	width: string;
}

const optionsCategorizationStatus = [
	{ id: 1, name: "Категорирование не проводилось", action: "NOT_CATEGORIZED" },
	{ id: 2, name: "Первичное категорирование начато", action: "PRIMARY_STARTED" },
	{ id: 3, name: "Первичное категорирование завершено", action: "PRIMARY_DONE" },
	{ id: 4, name: "Повторное категорирование начато", action: "SECONDARY_STARTED" },
	{ id: 5, name: "Повторное категорирование завершено", action: "SECONDARY_DONE" },
];

const EditionMeasures: FC<{ close: () => void }> = ({ close }) => {
	const [measures, setMeasures] = useState<string>("");
	const [submitError, setSubmitError] = useState<boolean>(false);
	const dispatch = useAppDispatch();
	const { selectedOrganization } = useAppSelector((state) => state.organizations);
	const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!measures.trim()) {
			setSubmitError(true);
		} else {
			setSubmitError(false);
			dispatch(
				editOrganization({
					organization: {
						...selectedOrganization,
						securityDescription: measures,
					},
				}),
			);
			close();
		}
	};

	useEffect(() => {
		if (selectedOrganization?.securityDescription) setMeasures(selectedOrganization.securityDescription);
		//eslint-disable-next-line
	}, []);

	return (
		<section className={styles.wrp}>
			<h4 className={styles.title}>Редактировать меры по обеспечению безопасности объекта</h4>
			<form onSubmit={submitHandler} className={styles.form}>
				<div className={styles["form-wrp"]}>
					<MyTextarea
						name="Меры по обеспечению безопасности объекта"
						placeholder="Введите наименование используемых мер безпасности"
						value={measures}
						set={(e) => setMeasures(e.target.value)}
						required={true}
						error={submitError && !measures.trim()}
					/>
				</div>
				<div className={styles.btns}>
					<Button onClick={close} type="button" variant={BUTTON_VARIANTS.OUTLINED} className={styles.button}>
						Отменить
					</Button>
					<Button type="submit" className={styles.button}>
						Готово
					</Button>
				</div>
			</form>
		</section>
	);
};

export const OrganizationPage: FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { selectedOrganization, subsidiariesList, iconSelectedOrganization, markerList, codeList } = useAppSelector(
		(state) => state.organizations,
	);
	const historyRef = useRef(null);

	const [historyParams, setHistoryParams] = useState<{
		page: number;
		num: number;
	}>({
		page: 1,
		num: 7,
	});

	const [documentName, setDocumentName] = useState<string | undefined>();
	const [path, setPath] = useState<string | undefined>();

	const [organizationsSearchValue, setOrganizationsValue] = useState<string>("");

	const [organizations, setOrganizations] = useState<{
		actual: IOrganizationShort[];
		temp: IOrganizationShort[];
	}>({
		actual: [],
		temp: [],
	});

	const [historyModal, setHistoryModal] = useState<boolean>(false);
	const [attentionModal, setAttentionModal] = useState<{
		show: boolean;
		data: OrganizationResponse | null;
	}>({
		show: !!organizations.actual.length,
		data: null,
	});
	const [headerMenu, setHeaderMenu] = useState<boolean>(false);

	const { organizationId } = useParams();

	const findedStatus = useMemo(() => {
		return optionsCategorizationStatus.find((option) => {
			return option.action === selectedOrganization?.categorizationStatus.status;
		});
	}, [selectedOrganization]);
	const [selectedStatus, setSelectedStatus] = useState<string | undefined>(findedStatus?.name);

	const [modalWindow, setModalWindow] = useState<IModalWindow>({
		content: null,
		visibility: false,
		width: "",
	});

	const handleHistoryChanges = (data: IHistoryChange[]) => {
		if (historyParams.page === 1) {
			if (data.length) {
				if (!(data.length < historyParams.num)) {
					setHistoryParams((prevState) => ({
						...prevState,
						page: prevState.page + 1,
					}));
				} else {
					setIsFinally(true);
				}
			}
			dispatch(setHistory(data));
		} else {
			if (data.length) {
				if (!(data.length < historyParams.num)) {
					setHistoryParams((prevState) => ({
						...prevState,
						page: prevState.page + 1,
					}));
				} else {
					setIsFinally(true);
				}
				dispatch(updateHistory(data));
			}
		}
	};

	const { setIsFinally, setFetch } = usePagination(
		`/audit/organization/${organizationId}/categorization-status`,
		historyParams,
		handleHistoryChanges,
		historyRef,
		[historyModal],
	);

	const clickHandler = () => {
		if (headerMenu) return;
		setTimeout(() => {
			setHeaderMenu((prev) => !prev);
		}, 1);
	};

	const closeModalWindow = () => {
		setModalWindow({
			...modalWindow,
			content: null,
			visibility: false,
			width: "default",
		});
	};

	const openModalWindow = (component: React.ReactElement, width: string) => {
		setModalWindow({
			...modalWindow,
			content: component,
			visibility: true,
			width,
		});
	};

	const submitChoosingDate = (date: string) => {
		dispatch(
			editOrganization({
				organization: {
					...selectedOrganization,
					registrationDate: date,
				},
			}),
		);
	};

	const openChoosingDate = () => {
		openModalWindow(
			<ChoosingDate
				title="Выбрать дату субъекта КИИ"
				subtitle="Субъект КИИ"
				defaultDate={selectedOrganization?.registrationDate || ""}
				close={closeModalWindow}
				onSubmit={(date) => submitChoosingDate(date)}
			/>,
			"default",
		);
	};

	const handleSwitchCategorization = (organization: OrganizationResponse) => {
		if (!organization.categorizable) {
			dispatch(
				switchCategorizationRule({
					orgId: organization.id,
					categorizable: organization.categorizable,
				}),
			);
		} else {
			organizationAPI.categorizableDetail(organization.id).then((data) => {
				setOrganizations((prevState) => ({
					...prevState,
					actual: data.data,
				}));
				if (data.data.length) {
					setAttentionModal({ show: !!data.data.length, data: organization });
				} else {
					let payload = {
						orgId: organization.id,
						categorizable: organization.categorizable!,
						children: data.data.length,
					};
					dispatch(switchCategorizationRule(payload));
				}
			});
		}
	};

	const handleShowHistoryModal = () => {
		setHistoryModal(true);
	};

	const getBaseData = (organizationId: string | undefined) => {
		dispatch(getSelectedOrganization(Number(organizationId)));
	};

	/**
	 * с вложенным children родитель не выключается при клике на "продолжить"
	 * @param organization
	 */
	const submitAttentional = (organization: OrganizationResponse | null) => {
		const candidate = organizations.actual.find((org) => org.id === organization?.id);

		if (organization) {
			dispatch(
				switchCategorizationRule({
					orgId: organization.id,
					categorizable: organization.categorizable,
					children: candidate ? organizations.actual.length : null,
				}),
			);
			setAttentionModal({ show: false, data: null });
		}
	};

	useEffect(() => {
		const regex = new RegExp(organizationsSearchValue, "gi");

		const tempArr: IOrganizationShort[] = organizations.actual.filter((organization) =>
			organization.fullName.match(regex),
		);

		setOrganizations((prevState) => ({
			...prevState,
			temp: [...tempArr],
		}));
		//eslint-disable-next-line
	}, [organizationsSearchValue]);

	useEffect(() => {
		setAttentionModal({ show: false, data: null });
	}, [location.pathname]);

	useEffect(() => {
		getBaseData(organizationId);
		dispatch(getMarkerList({ id: Number(organizationId) }));
		dispatch(getCodeList(Number(organizationId)));
		return () => {
			dispatch(ClearSelectedOrganization());
		};
		//eslint-disable-next-line
	}, [organizationId]);

	useEffect(() => {
		if (selectedOrganization?.logoExists && selectedOrganization.id === Number(organizationId))
			dispatch(getOrganizationLogo(Number(organizationId)));
		if (selectedOrganization?.childrenCount && selectedOrganization.id === Number(organizationId)) {
			dispatch(getSubsidiaries(Number(organizationId)));
		}
		if (selectedOrganization) {
			const selectStatus = optionsCategorizationStatus.find(
				(status) => status.action === selectedOrganization.categorizationStatus.status,
			);
			if (selectStatus) {
				setSelectedStatus(selectStatus.name);
				setHistoryParams((prevState) => ({ ...prevState, page: 1 }));
				setIsFinally(false);
				setFetch(true);
			}
		}
		//eslint-disable-next-line
	}, [selectedOrganization]);

	useEffect(() => {
		if (historyModal) {
			setHistoryParams((prevState) => ({ ...prevState, page: 1 }));
			setIsFinally(false);
			setFetch(true);
		}
		//eslint-disable-next-line
	}, [historyModal]);

	return (
		<>
			{modalWindow.visibility && (
				<ModalWindow close={closeModalWindow} width={modalWindow.width}>
					{modalWindow.content}
				</ModalWindow>
			)}
			<main className={styles.content}>
				<section className={styles.card}>
					<div className={styles.header}>
						<div className={styles["header-wrp"]}>
							<div onClick={() => navigate(-1)} className={styles.backIcon}>
								<BackArrowIcon />
							</div>
							<span className={styles.title}>Карточка организации</span>
						</div>
						<div className={styles["header-item"]}>
							{/* /documents/1/folders */}
							<Link
								state={{ name: selectedOrganization?.fullName }}
								className={styles["header-btn"]}
								to={`/documents/current/${selectedOrganization?.id}/folders`}>
								<Button variant={BUTTON_VARIANTS.OUTLINED}>Перейти к документам организации</Button>
							</Link>
							<Button
								disabled={selectedOrganization?.archived}
								className={styles["header-btn"]}
								to={`/references/staff?org=${selectedOrganization?.id}`}>
								Сотрудники организации
							</Button>
							<div className={styles["header-menu-wrp"]}>
								<button onClick={clickHandler} className={cn(styles["header-btn"], styles["header-menu-btn"])}>
									<ThreeDots />
								</button>
								{headerMenu && <Menu close={() => setHeaderMenu(false)} />}
							</div>
						</div>
					</div>
					<div className={styles["card-content"]}>
						<div className={styles.info}>
							<div className={styles["info-wrp"]}>
								<div className={styles["info-header"]}>
									<div className={styles["info-header-logo-wrp"]}>
										<div className={styles["logo-wrp"]}>
											<div
												onClick={() => openModalWindow(<ChangingLogo close={closeModalWindow} />, "default")}
												className={cn(styles["logo-icon"], selectedOrganization?.archived && styles.disabled)}>
												{iconSelectedOrganization ? (
													<img className={styles["logo-icon"]} src={iconSelectedOrganization} alt="logo" />
												) : (
													<LogoField />
												)}
											</div>
										</div>
										<span className={cn(styles.subtitle, styles.shortName)}>{selectedOrganization?.shortName}</span>
									</div>
									<div className={styles["info-header-status"]}>
										<Tooltip
											show={!historyModal}
											menu={<ShortHistoryChanges showHistoryModal={handleShowHistoryModal} />}
											color="themeWhite"
											position="top-left">
											<InfoIcon className={styles.infoIconBig} />
										</Tooltip>

										<InputTemp
											className={styles.select}
											type="select"
											wordWrap={"wrap"}
											value={selectedStatus || ""}
											onSelect={(e) => {
												dispatch(
													changeStatusCategorization({
														orgId: organizationId,
														status: e.action,
													}),
												).then(() => {
													getBaseData(organizationId);
												});
											}}
											options={optionsCategorizationStatus}
											field="name"
											disabled={selectedOrganization?.archived}
										/>
									</div>
								</div>
								<div className={styles["info-basic"]}>
									<div className={styles["info-basic-header"]}>
										<span className={styles["info-basic-title"]}>Основная информация</span>
										<div className={styles["info-basic-header-item"]}>
											<div className={styles["info-basic-header-item-wrp"]}>
												<span>Субъект КИИ</span>
												<div
													onClick={openChoosingDate}
													className={cn(
														styles["info-basic-header-item-btn"],
														selectedOrganization?.archived && styles["info-basic-header-item-btn-disabled"],
													)}>
													{/* <div onClick={() => openModalWindow(<ChoosingDate title="Выбрать дату субъекта КИИ" subtitle="Субъект КИИ" date="" close={closeModalWindow} />, "default")} className={styles["info-basic-header-item-btn"]}> */}
													<div>
														<CalendarIcon />
													</div>
													<span>{convertDate(selectedOrganization?.registrationDate || "") || "Добавить дату"}</span>
												</div>
											</div>
											<a
												href={selectedOrganization?.website!}
												target="_blank"
												className={cn(
													styles["info-basic-header-item-btn"],
													!selectedOrganization?.website ? styles["info-basic-header-item-btn-disabled"] : "",
												)}
												rel="noreferrer">
												<div>
													<PlanetIcon />
												</div>
												<span>Сайт</span>
											</a>
										</div>
									</div>

									<div className={styles["info-basic-data"]}>
										<div className={styles["info-basic-data-wrp"]}>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>ИНН</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{selectedOrganization?.inn || "—"}
												</span>
											</div>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>Тип организации по иерархии</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{selectedOrganization?.type.name}
												</span>
											</div>
										</div>
										<div className={styles["info-basic-data-wrp"]}>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>КПП</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{selectedOrganization?.kpp || "—"}
												</span>
											</div>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>Тип организации</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{selectedOrganization?.propertyType?.type || "—"}
												</span>
											</div>
										</div>
										<div className={styles["info-basic-data-wrp"]}>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>ОГРН</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{selectedOrganization?.ogrn || "—"}
												</span>
											</div>
											<div className={styles["info-basic-data-item"]}>
												<span className={styles["info-basic-data-item-title"]}>Номер телефона</span>
												<span className={styles["info-basic-data-item-subtitle"]}>
													{replacePhone(selectedOrganization?.phone, "to_front") || "—"}
												</span>
											</div>
										</div>
									</div>
									<div className={styles["info-activities"]}>
										<div className={styles["info-activities-header"]}>
											<span className={styles["info-activities-title"]}>Маркеры</span>
											<span
												onClick={() =>
													openModalWindow(
														<SettingMarkers
															documentName={documentName}
															path={path}
															close={closeModalWindow}
															setDocumentName={setDocumentName}
															setPath={setPath}
														/>,
														"middle",
													)
												}
												className={cn(
													styles["info-activities-edit"],
													selectedOrganization?.archived && styles.disabled,
												)}>
												Редактировать
											</span>
										</div>
										<div data-comment-false={true} className={styles["info-activities-list"]}>
											{markerList.user.length || !isEmpty(markerList.system) ? (
												[
													markerList.user.map((marker) => (
														<div key={marker.id} className={styles.marker}>
															<Badge color={marker.colorCode || ""}>{marker.abbreviation}</Badge>
														</div>
													)),
													!isEmpty(markerList.system) && (
														<div className={styles.marker}>
															<Badge color={"#35A440"}>{markerList.system.name}</Badge>
														</div>
													),
												]
											) : (
												<span className={styles["info-activities-list-empty"]}>—</span>
											)}
										</div>
									</div>
								</div>
								<div className={styles["info-details"]}>
									<span className={styles["info-details-title"]}>Подробности</span>
									<div className={styles["info-details-data"]}>
										<div className={styles["info-details-data-item"]}>
											<span className={styles["info-details-data-item-title"]}>Полное наименование организации</span>
											<span className={styles["info-details-data-item-subtitle"]}>
												{selectedOrganization?.fullName || "—"}
											</span>
										</div>
										<div className={styles["info-details-data-item"]}>
											<span className={styles["info-details-data-item-title"]}>Юридический адрес</span>
											<span className={styles["info-details-data-item-subtitle"]}>
												{selectedOrganization?.legalAddress || "—"}
											</span>
										</div>
										<div className={styles["info-details-data-item"]}>
											<span className={styles["info-details-data-item-title"]}>Почтовый адрес</span>
											<span className={styles["info-details-data-item-subtitle"]}>
												{selectedOrganization?.postalAddress || "—"}
											</span>
										</div>
										<div className={styles["info-details-data-item"]}>
											<span className={styles["info-details-data-item-title"]}>Сфера деятельности (по 187 ФЗ)</span>
											<span className={styles["info-details-data-item-subtitle"]}>
												{selectedOrganization?.activityArea?.name || "—"}
											</span>
										</div>
										<div className={styles["info-details-data-item"]}>
											<span className={styles["info-details-data-item-title"]}>
												Ответственный за информационную безопасность
											</span>
											{selectedOrganization?.responsible ? (
												<>
													<Link
														to={`/employee/${selectedOrganization?.responsible.id}`}
														className={cn(styles.link, styles["info-details-data-item-subtitle"])}>
														{connectNames(selectedOrganization?.responsible)}
													</Link>
													<span className={styles["info-details-data-item-subtitle-2"]}>
														{selectedOrganization?.responsible.position}
													</span>
												</>
											) : (
												"—"
											)}
										</div>
									</div>
								</div>
								<div className={styles["info-activities-wrp"]}>
									<div className={styles["info-activities"]}>
										<div className={styles["info-activities-header"]}>
											<span className={styles["info-activities-title"]}>Вид деятельности организации (коды ОКВЭД)</span>
											<Link
												to={`/references/codes/${organizationId}/edit`}
												className={cn(
													styles["info-activities-edit"],
													selectedOrganization?.archived && styles.disabled,
												)}>
												Редактировать
											</Link>
										</div>
										<div data-comment-false={true} className={styles["info-activities-list"]}>
											{codeList.length ? (
												codeList.map((code, i) => (
													<div key={i} className={styles["code-wrp"]}>
														<div className={styles.code}>{code.okved}</div>
													</div>
												))
											) : (
												<span className={styles["info-activities-list-empty"]}>—</span>
											)}
										</div>
									</div>
								</div>
								<div className={styles["info-activities-wrp"]}>
									<div className={styles["info-activities"]}>
										<div className={styles["info-activities-header"]}>
											<span className={styles["info-activities-title"]}>
												Организационные меры по обеспечению безопасности объекта
											</span>
											<span
												onClick={() => openModalWindow(<EditionMeasures close={closeModalWindow} />, "default")}
												className={cn(
													styles["info-activities-edit"],
													selectedOrganization?.archived && styles.disabled,
												)}>
												Редактировать
											</span>
										</div>
										<div data-comment-false={true} className={styles["info-activities-list"]}>
											{/* <span className={styles["info-activities-list-empty"]}> */}
											<span className={styles.securityDescription}>
												{selectedOrganization?.securityDescription || "—"}
											</span>
										</div>
									</div>
								</div>
							</div>
							<div className={styles["info-footer"]}>
								<div className={styles["info-footer-item"]}>
									<span className={styles["info-footer-item-title"]}>Родительская организация</span>
									{selectedOrganization?.parent ? (
										<Link
											to={`/organization/${selectedOrganization?.parent?.id}`}
											className={cn(styles["info-footer-item-subtitle"], styles.link)}>
											{selectedOrganization?.parent?.shortName}
										</Link>
									) : (
										<span className={styles["info-footer-item-subtitle"]}>—</span>
									)}
								</div>
								<div className={styles["info-footer-item"]}>
									<span className={styles["info-footer-item-title"]}>Генеральный директор</span>
									{selectedOrganization?.director ? (
										<Link
											to={`/employee/${selectedOrganization?.director.id}`}
											className={cn(styles["info-footer-item-subtitle"], styles.link)}>
											{connectNames(selectedOrganization?.director)}
										</Link>
									) : (
										<span className={styles["info-footer-item-subtitle"]}>—</span>
									)}
								</div>
							</div>
						</div>
						<div className={styles.subsidiaries}>
							<div className={styles["subsidiaries-header"]}>
								<span className={styles.subtitle}>Структура организации:</span>
								{selectedOrganization?.type.nestingIndex !== 3 && (
									<div
										onClick={() =>
											openModalWindow(
												<AddingOrganization
													close={closeModalWindow}
													title="Добавление карточки организации"
													parentId={selectedOrganization?.id}
												/>,
												"default",
											)
										}
										className={cn(styles["subsidiaries-add-btn"], selectedOrganization?.archived && styles.disabled)}>
										<div className={styles["subsidiaries-add-btn-icon"]}>
											<PlusBoldIcon />
										</div>
										<span className={styles["subsidiaries-add-btn-txt"]}>Добавить</span>
									</div>
								)}
							</div>
							{selectedOrganization?.childrenCount ? (
								<table className={styles.table}>
									<thead className={styles.head}>
										<tr className={styles["head-row"]}>
											<th className={styles["head-item"]}>Наименование организации</th>
											<th className={styles["head-item"]}>ОГРН</th>
											<th className={styles["head-item"]}>ТИП</th>
											<th className={cn(styles["head-item"], styles["head-item--icon"])}>
												<div>
													<Tooltip
														color="themeWhite"
														position="top-left"
														menu={
															<div className={styles.infoBox}>
																Запретите категорирование, если необходимо проводить учет ОКИИ на уровне материнской
																компании. Если переключатель серого цвета - категорирование отключено.
															</div>
														}>
														<InfoIcon className={styles.infoIcon} />
													</Tooltip>
													Настройки категорирования
												</div>
											</th>
										</tr>
									</thead>
									<tbody data-comment-false={true} className={styles.body}>
										{/* .sort((a, b) => a.type.positionIndex - b.type.positionIndex) */}
										{[...subsidiariesList]
											.sort((a, b) => a?.type?.positionIndex - b?.type?.positionIndex)
											.map((item) => (
												<tr key={item.id} className={styles["body-row"]}>
													<td className={styles["body-item"]}>
														<Link to={`/organization/${item.id}`} className={styles.link}>
															{item.shortName}
														</Link>
													</td>
													<td className={styles["body-item"]}>{item.ogrn || "—"}</td>
													<td className={styles["body-item"]}>{item.type.name}</td>
													<td className={styles["body-item"]}>
														<Switch checked={item.categorizable} onChange={() => handleSwitchCategorization(item)} />
													</td>
												</tr>
											))}
									</tbody>
								</table>
							) : (
								<div className={styles.warning}>
									<span className={styles["warning-txt"]}>У вас нет дочерних организаций</span>
								</div>
							)}
						</div>
					</div>
				</section>
				{historyModal && <HistoryChanges close={() => setHistoryModal(false)} ref={historyRef} />}

				{attentionModal.show && (
					<AttentionModal
						searchValue={organizationsSearchValue}
						handleSearchValue={(e) => setOrganizationsValue(e.target.value)}
						organizations={organizations}
						submit={() => submitAttentional(attentionModal.data)}
						close={() => setAttentionModal({ show: false, data: null })}
					/>
				)}
			</main>
		</>
	);
};
